import { useInfiniteQuery, type QueryKey } from '@tanstack/react-query';

import type { Page } from '@/types/api';

interface UseInfiniteListParams<T, TParams> {
  queryKey: QueryKey;
  queryFn: (params: TParams & { cursor?: string; signal?: AbortSignal }) => Promise<Page<T>>;
  params: TParams;
  enabled?: boolean;
}

/**
 * Wrapper around TanStack's useInfiniteQuery for cursor-based paginated endpoints.
 *
 * Returns flattened items + helpers to load next page.
 *
 * Usage:
 *   const { items, fetchNextPage, hasNextPage, isLoading } = useInfiniteList({
 *     queryKey: ['profesionales', { q: 'foo' }],
 *     queryFn: profesionalesApi.list,
 *     params: { q: 'foo' },
 *   });
 */
export function useInfiniteList<T, TParams extends object>({
  queryKey,
  queryFn,
  params,
  enabled = true,
}: UseInfiniteListParams<T, TParams>) {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      queryFn({ ...params, cursor: pageParam as string | undefined, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled,
  });

  const items = query.data?.pages.flatMap((page) => page.items) ?? [];

  return {
    items,
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
